import { API_BASE } from './api-base';
import { INSTANCE_AUTH_SPEC, toggleOverride } from './dev-feature-toggles';
/**
 * Client read of the `symphonyInstanceAuthWeb` toggle — the single gate for the
 * signed-instance auth path (docs §6). Fetched once from the server conduct
 * route and cached for the page load. Fails safe to `false` (off).
 *
 * A dev-tools **Feature toggles** override wins over the conducted value, so
 * forcing it On/Off exercises the real flow rather than a special case. That
 * override is inert without a dev-tools grant (lib/dev-feature-toggles.ts), so a
 * real user always gets the conducted value.
 */

const LOG = '[instance-auth]';
// Last resolved flag, persisted so the boot can decide synchronously whether to
// show the loader without waiting on the flag round-trip (see use-instance-bootstrap).
const FLAG_STORAGE_KEY = 'symphonyInstanceAuthWeb:enabled';

let cached: Promise<boolean> | null = null;

function persist(enabled: boolean): void {
  try {
    window.localStorage.setItem(FLAG_STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    /* private-mode / SSR — the in-memory cache still serves this load */
  }
}

/** Last-known flag from a previous load (sync), or null if never resolved. */
export function lastKnownInstanceAuthEnabled(): boolean | null {
  const forced = toggleOverride(INSTANCE_AUTH_SPEC);
  if (forced !== undefined) return forced === 'true';
  try {
    const v = window.localStorage.getItem(FLAG_STORAGE_KEY);
    return v === '1' ? true : v === '0' ? false : null;
  } catch {
    return null;
  }
}

export function instanceAuthEnabled(): Promise<boolean> {
  // Checked on every call, not memoized: the override can be set mid-session from the dev panel.
  const forced = toggleOverride(INSTANCE_AUTH_SPEC);
  if (forced !== undefined) return Promise.resolve(forced === 'true');
  if (cached) return cached;
  cached = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/flags/instance-auth`, { credentials: 'include' });
      if (!res.ok) return false;
      const enabled = ((await res.json()) as boolean) === true;
      persist(enabled);
      if (enabled) console.info(`${LOG} flow ENABLED (symphonyInstanceAuthWeb)`);
      return enabled;
    } catch {
      return false;
    }
  })();
  return cached;
}
